import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaUserPlus,
  FaTimes,
  FaHandshake,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList,
} from 'react-icons/fa';
import {AuthContext} from '../../components/AuthContext';
import MasterSelection from '../../components/MasterSelection';
import QuotationSelection from '../../components/QuotationSelection';
import GSTVerificationModal from '../../components/GSTVerificationModal';
import PANVerificationModal from '../../components/PANVerificationModal';

const API = process.env.REACT_APP_API_BASE_URL;

const ITEM_PRESETS = ['DOOR', 'WINDOWS', 'FURNITURE', 'KITCHEN', 'SANITARY', 'TILES'];
const RESPONSES = ['Positive', 'Negative', 'Follow-up'];
const LEAD_STATUSES = [
  'Visit',
  'Quotation To Be Issued',
  'Quotation Issued',
  'Quotation Revision',
  'Purchased',
];
const SALUTATION_OPTIONS = ['Mr.', 'Mrs.', 'M/s.', 'Miss.', 'Dr.', 'Prof.'].map(s => ({
  value: s,
  label: s,
}));

const pageSizeOptions = [
  {value: 5, label: '5'},
  {value: 10, label: '10'},
  {value: 20, label: '20'},
  {value: 50, label: '50'},
  {value: 100, label: '100'},
];

const fmtDate = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
const startOfMonthStr = () => {
  const n = new Date();
  return fmtDate(new Date(n.getFullYear(), n.getMonth(), 1));
};
const endOfMonthStr = () => {
  const n = new Date();
  return fmtDate(new Date(n.getFullYear(), n.getMonth() + 1, 0));
};

// Human-readable date, e.g. "07 Mar 2026" (avoids ambiguous ISO like 2026-03-07).
const formatDisplayDate = value => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Human-readable date + time, e.g. "07 Mar 2026, 03:20 pm".
const formatDisplayDateTime = value => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return `${formatDisplayDate(d)}, ${d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const emptyVisitor = {
  salutation: '',
  name: '',
  primaryMobile: '',
  secondaryMobile: '',
  email: '',
  dob: '',
  station: '',
  pinCode: '',
  address: '',
  gstNo: '',
  panNo: '',
  reference: '',
  referenceName: '',
  salesperson: '',
  remarks: '',
};

const pad2 = n => String(n).padStart(2, '0');
const toLocalInput = value => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
    d.getDate(),
  )}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const nowLocalInput = () => toLocalInput(new Date());

const emptyVisit = {
  visitedAt: nowLocalInput(),
  salespersons: [],
  itemsOfInterest: [],
};

const emptyResponse = {
  response: '',
  leadStatus: 'Visit',
  remarks: '',
  quotation: '',
  quotationType: '',
  quotationLabel: '',
};

const QUOTATION_LEAD_STATUSES = ['Quotation Issued', 'Quotation Revision'];

const statusColor = {
  Positive: 'bg-green-100 text-green-700',
  Negative: 'bg-red-100 text-red-700',
  'Follow-up': 'bg-amber-100 text-amber-700',
  Pending: 'bg-gray-100 text-gray-600',
};

const customSelectStyles = darkMode => ({
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    backgroundColor: darkMode ? '#4b5563' : 'white',
    border: `1px solid ${darkMode ? '#6b7280' : '#d1d5db'}`,
    borderRadius: 8,
    boxShadow: state.isFocused
      ? `0 0 0 3px ${darkMode ? 'rgba(129,140,248,0.25)' : 'rgba(99,102,241,0.15)'}`
      : 'none',
    '&:hover': {borderColor: darkMode ? '#9ca3af' : '#9ca3af'},
  }),
  menu: base => ({
    ...base,
    backgroundColor: darkMode ? '#1f2937' : 'white',
    zIndex: 3000,
  }),
  menuPortal: base => ({...base, zIndex: 3000}),
  singleValue: base => ({
    ...base,
    color: darkMode ? '#f3f4f6' : '#374151',
  }),
  input: base => ({...base, color: darkMode ? '#f3f4f6' : '#374151'}),
  placeholder: base => ({...base, color: '#9ca3af'}),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#6366f1'
      : state.isFocused
      ? darkMode
        ? '#374151'
        : '#eef2ff'
      : darkMode
      ? '#1f2937'
      : 'white',
    color: state.isSelected ? 'white' : darkMode ? '#f3f4f6' : '#374151',
  }),
  multiValue: base => ({
    ...base,
    backgroundColor: darkMode ? '#4338ca' : '#e0e7ff',
  }),
  multiValueLabel: base => ({
    ...base,
    color: darkMode ? '#e0e7ff' : '#3730a3',
  }),
});

const VisitorManagement = () => {
  const {user} = useContext(AuthContext);
  const companyId = user?.user?.company?._id || user?.user?.company;
  const createdBy = user?.user?._id;

  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('darkMode') === 'true',
  );

  const [visitors, setVisitors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stations, setStations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterFrom, setFilterFrom] = useState(startOfMonthStr);
  const [filterTo, setFilterTo] = useState(endOfMonthStr);
  const [filterStation, setFilterStation] = useState('');
  const [filterResponse, setFilterResponse] = useState('');
  const [filterLeadStatus, setFilterLeadStatus] = useState('');
  const [filterAttendedByMe, setFilterAttendedByMe] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyVisitor);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [showGSTVerification, setShowGSTVerification] = useState(false);
  const [showPANVerification, setShowPANVerification] = useState(false);
  const [isPanReadOnly, setIsPanReadOnly] = useState(false);
  const [isGstReadOnly, setIsGstReadOnly] = useState(false);
  const [showGstPanFields, setShowGstPanFields] = useState(false);

  const [selected, setSelected] = useState(null);
  const [visits, setVisits] = useState([]);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [visitForm, setVisitForm] = useState(emptyVisit);
  const [visitSubmitting, setVisitSubmitting] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseVisit, setResponseVisit] = useState(null);
  const [responseForm, setResponseForm] = useState(emptyResponse);
  const [responseSubmitting, setResponseSubmitting] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignVisit, setAssignVisit] = useState(null);
  const [assignSalespersons, setAssignSalespersons] = useState([]);
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  const currentUserId = user?.user?._id || user?._id;

  // Sync dark mode with the global toggle (other pages persist it in localStorage)
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const fetchVisitors = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/visitors`, {
        params: {
          companyId,
          search: searchQuery,
          station: filterStation,
          response: filterResponse,
          leadStatus: filterLeadStatus,
          attendedBy: filterAttendedByMe ? currentUserId : '',
          from: filterFrom,
          to: filterTo,
          page: currentPage,
          limit: pageSize,
        },
      });
      setVisitors(res.data?.data || []);
      setTotalPages(res.data?.pagination?.totalPages || 1);
      setTotalCount(res.data?.pagination?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching visitors:', error);
      setVisitors([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    if (!companyId) return;
    try {
      const [empRes, stationRes] = await Promise.all([
        axios.get(`${API}/employees/company2/${companyId}`),
        axios.get(`${API}/stations`),
      ]);
      setEmployees(
        Array.isArray(empRes.data)
          ? empRes.data
          : empRes.data?.employees || empRes.data?.data || [],
      );
      setStations(stationRes.data?.stations || stationRes.data || []);
    } catch (error) {
      console.error('Error fetching lookups:', error);
    }
  };

  useEffect(() => {
    fetchLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    const t = setTimeout(fetchVisitors, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    currentPage,
    pageSize,
    searchQuery,
    filterStation,
    filterResponse,
    filterLeadStatus,
    filterAttendedByMe,
    filterFrom,
    filterTo,
  ]);

  const stationOptions = stations.map(s => ({value: s._id, label: s.name}));
  const employeeOptions = employees.map(e => ({value: e._id, label: e.name}));

  // ── GST / PAN validation (same as Customer Management) ──────────────────────
  const validateGSTAndExtractPAN = async gstValue => {
    if (!gstValue || gstValue.length !== 15) {
      return {isValid: false, error: 'GST number must be 15 characters long'};
    }
    try {
      const response = await axios.post(`${API}/customers/validate-gst`, {
        gstNo: gstValue,
      });
      if (response.data.isValid) return {isValid: true, panNo: response.data.panNo};
      return {isValid: false, error: 'Invalid GST number'};
    } catch (error) {
      return {
        isValid: false,
        error: error.response?.data?.error || 'Invalid GST number',
      };
    }
  };

  const handleGSTChange = async e => {
    const gstValue = e.target.value.toUpperCase();
    setForm(prev => ({...prev, gstNo: gstValue}));
    if (gstValue.length === 15) {
      const result = await validateGSTAndExtractPAN(gstValue);
      if (result.isValid) {
        setForm(prev => ({...prev, gstNo: gstValue, panNo: result.panNo}));
        setIsPanReadOnly(true);
        Swal.fire({
          icon: 'success',
          title: 'Valid GST',
          text: `PAN ${result.panNo} extracted from GST`,
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        setIsPanReadOnly(false);
        Swal.fire({icon: 'error', title: 'Invalid GST', text: result.error});
      }
    } else {
      setIsPanReadOnly(false);
    }
  };

  const handlePANChange = e =>
    setForm(prev => ({...prev, panNo: e.target.value.toUpperCase()}));

  const handleClearGST = () => {
    setForm(prev => ({...prev, gstNo: '', panNo: ''}));
    setIsPanReadOnly(false);
    setIsGstReadOnly(false);
    setShowGstPanFields(false);
  };

  const handleGSTVerificationData = ({gstData, gstin, useData}) => {
    if (!useData || !gstData) return;
    const extractedData = gstData.extractedData || {};
    const extractedPAN = gstin.substring(2, 12);
    setForm(prev => ({
      ...prev,
      gstNo: gstin,
      panNo: extractedPAN,
      name: prev.name || extractedData.legalName || extractedData.tradeName || '',
      address: prev.address || extractedData.address || '',
    }));
    setIsPanReadOnly(true);
    setIsGstReadOnly(true);
    setShowGstPanFields(true);
    Swal.fire({icon: 'success', title: 'GST Verified', text: `PAN ${extractedPAN} extracted`});
  };

  const handlePANVerificationData = ({panNo}) => {
    setForm(prev => ({...prev, panNo}));
    setIsPanReadOnly(true);
    setShowGstPanFields(true);
    Swal.fire({icon: 'success', title: 'PAN Verified', text: panNo, timer: 1500, showConfirmButton: false});
  };

  // ── Visitor CRUD ────────────────────────────────────────────────────────────
  const resetForm = () => {
    setForm(emptyVisitor);
    setIsEditing(false);
    setEditingId(null);
    setIsPanReadOnly(false);
    setIsGstReadOnly(false);
    setShowGstPanFields(false);
  };

  const startEditing = visitor => {
    setForm({
      ...emptyVisitor,
      ...visitor,
      station: visitor.station?._id || visitor.station || '',
      reference: visitor.reference?._id || visitor.reference || '',
      referenceName: visitor.reference?.name || '',
      salesperson: visitor.salesperson?._id || visitor.salesperson || '',
      dob: visitor.dob ? String(visitor.dob).slice(0, 10) : '',
    });
    setIsEditing(true);
    setEditingId(visitor._id);
    setIsPanReadOnly(!!(visitor.panNo && visitor.gstNo));
    setIsGstReadOnly(false);
    setShowGstPanFields(!!(visitor.gstNo || visitor.panNo));
    if (typeof window !== 'undefined') window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const saveVisitor = async () => {
    if (!form.name.trim() || !form.primaryMobile.trim()) {
      Swal.fire('Validation', 'Name and primary mobile are required', 'warning');
      return;
    }
    if (!/^\d{10}$/.test(form.primaryMobile.trim())) {
      Swal.fire('Validation', 'Primary mobile must be exactly 10 digits', 'warning');
      return;
    }
    if (form.secondaryMobile && !/^\d{10}$/.test(form.secondaryMobile.trim())) {
      Swal.fire('Validation', 'Secondary mobile must be exactly 10 digits', 'warning');
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      Swal.fire('Validation', 'Please enter a valid email address', 'warning');
      return;
    }
    if (form.pinCode && !/^\d{6}$/.test(form.pinCode)) {
      Swal.fire('Validation', 'PIN code must be exactly 6 digits', 'warning');
      return;
    }
    if (form.gstNo && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gstNo)) {
      Swal.fire('Validation', 'Please enter a valid GSTIN', 'warning');
      return;
    }
    if (form.panNo && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNo)) {
      Swal.fire('Validation', 'Please enter a valid PAN (e.g., ABCDE1234F)', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {...form, createdBy};
      delete payload.referenceName;
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      });
      if (isEditing) {
        await axios.put(`${API}/visitors/${editingId}`, {...payload, updatedBy: createdBy});
      } else {
        await axios.post(`${API}/visitors`, payload);
      }
      Swal.fire('Success', isEditing ? 'Visitor updated' : 'Visitor created', 'success');
      resetForm();
      fetchVisitors();
    } catch (error) {
      Swal.fire(
        'Validation',
        (error.response?.data?.errors || []).join('\n') ||
          error.response?.data?.message ||
          'Failed to save visitor',
        'error',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteVisitor = async visitor => {
    const confirm = await Swal.fire({
      title: 'Delete visitor?',
      text: 'This is allowed only when the visitor has no visits or quotations.',
      icon: 'warning',
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.delete(`${API}/visitors/${visitor._id}`);
      if (selected?._id === visitor._id) setSelected(null);
      fetchVisitors();
    } catch (error) {
      Swal.fire('Cannot delete', error.response?.data?.message || 'Failed to delete visitor', 'error');
    }
  };

  // ── Visits ──────────────────────────────────────────────────────────────────
  const loadVisits = async (visitorId, range = {}) => {
    try {
      const res = await axios.get(`${API}/visits/visitor/${visitorId}`, {
        params: {
          from: range.from || '',
          to: range.to || '',
        },
      });
      setVisits(res.data?.data || []);
    } catch (error) {
      console.error('Error fetching visits:', error);
      setVisits([]);
    }
  };

  const openInteractions = async visitor => {
    setSelected(visitor);
    await loadVisits(visitor._id);
  };

  // A visit is editable / response-updatable only by a salesperson who attended it.
  const isAssigned = visit => (visit?.salespersons || []).length > 0;
  const canManageVisit = visit => {
    const ids = (visit?.salespersons || []).map(s => s._id || s);
    return ids.length > 0 && ids.includes(currentUserId);
  };

  const openAssignModal = visit => {
    setAssignVisit(visit);
    setAssignSalespersons((visit.salespersons || []).map(s => s._id || s));
    setShowAssignModal(true);
  };

  const saveAssign = async () => {
    if (assignSalespersons.length === 0) {
      Swal.fire('Validation', 'Please select at least one salesperson', 'warning');
      return;
    }
    setAssignSubmitting(true);
    try {
      await axios.put(`${API}/visits/${assignVisit._id}`, {
        visitedAt: toLocalInput(assignVisit.visitedAt),
        itemsOfInterest: assignVisit.itemsOfInterest || [],
        salespersons: assignSalespersons,
        updatedBy: currentUserId,
      });
      setShowAssignModal(false);
      loadVisits(selected._id);
      fetchVisitors();
      Swal.fire('Saved', 'Salesperson assigned', 'success');
    } catch (error) {
      Swal.fire(
        'Validation',
        (error.response?.data?.errors || []).join('\n') ||
          error.response?.data?.message ||
          'Failed to assign salesperson',
        'error',
      );
    } finally {
      setAssignSubmitting(false);
    }
  };

  const openVisitModal = visit => {
    setEditingVisit(visit || null);
    setVisitForm(
      visit
        ? {
            visitedAt: toLocalInput(visit.visitedAt),
            salespersons: (visit.salespersons || []).map(s => s._id || s),
            itemsOfInterest: visit.itemsOfInterest || [],
          }
        : {
            ...emptyVisit,
            salespersons: currentUserId ? [currentUserId] : [],
          },
    );
    setShowVisitModal(true);
  };

  const saveVisit = async () => {
    setVisitSubmitting(true);
    try {
      const payload = {
        visitedAt: visitForm.visitedAt,
        salespersons: visitForm.salespersons,
        itemsOfInterest: visitForm.itemsOfInterest,
      };
      if (editingVisit) {
        await axios.put(`${API}/visits/${editingVisit._id}`, {
          ...payload,
          updatedBy: currentUserId,
        });
      } else {
        await axios.post(`${API}/visits`, {
          ...payload,
          visitor: selected._id,
          response: 'Pending',
          createdBy: currentUserId,
        });
      }
      setShowVisitModal(false);
      loadVisits(selected._id);
      fetchVisitors();
      Swal.fire(
        'Saved',
        editingVisit ? 'Interaction updated' : 'Interaction recorded',
        'success',
      );
    } catch (error) {
      Swal.fire(
        'Validation',
        (error.response?.data?.errors || []).join('\n') ||
          error.response?.data?.message ||
          'Failed to save interaction',
        'error',
      );
    } finally {
      setVisitSubmitting(false);
    }
  };

  const openResponseModal = visit => {
    setResponseVisit(visit);
    setResponseForm({
      response: visit.response === 'Pending' ? '' : visit.response,
      leadStatus: visit.leadStatus || 'Visit',
      remarks: visit.remarks || '',
      quotation: visit.quotation || '',
      quotationType: visit.quotationType || '',
      quotationLabel: visit.quotation?.id || '',
    });
    setShowResponseModal(true);
  };

  const saveResponse = async () => {
    const needsQuotation = QUOTATION_LEAD_STATUSES.includes(
      responseForm.leadStatus,
    );
    if (needsQuotation && !responseForm.quotation) {
      Swal.fire(
        'Quotation required',
        'Please select the quotation to link to this visit',
        'warning',
      );
      return;
    }
    setResponseSubmitting(true);
    try {
      await axios.put(`${API}/visits/${responseVisit._id}/status`, {
        response: responseForm.response || 'Pending',
        leadStatus: responseForm.leadStatus,
        remarks: responseForm.remarks,
        quotation: needsQuotation ? responseForm.quotation : null,
        quotationType: needsQuotation ? responseForm.quotationType || null : null,
        updatedBy: currentUserId,
      });
      setShowResponseModal(false);
      loadVisits(selected._id);
      fetchVisitors();
      Swal.fire('Saved', 'Client response updated', 'success');
    } catch (error) {
      Swal.fire(
        'Validation',
        (error.response?.data?.errors || []).join('\n') ||
          error.response?.data?.message ||
          'Failed to update client response',
        'error',
      );
    } finally {
      setResponseSubmitting(false);
    }
  };

  const deleteVisit = async visit => {
    const confirm = await Swal.fire({
      title: 'Delete interaction?',
      icon: 'warning',
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;
    await axios.delete(`${API}/visits/${visit._id}`);
    loadVisits(selected._id);
    fetchVisitors();
  };

  const labelCls = `font-semibold mb-1 text-sm ${
    darkMode ? 'text-gray-200' : 'text-gray-700'
  }`;
  const inputCls = `border rounded-lg p-2.5 w-full transition-colors duration-300 ${
    darkMode
      ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
  }`;
  const cardCls = darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 shadow-md';

  const renderPagination = () => {
    const from = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const to = Math.min(currentPage * pageSize, totalCount);
    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p += 1) pages.push(p);
    return (
      <div
        className={`mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 ${
          darkMode ? 'text-gray-200' : 'text-gray-700'
        }`}>
        <div className="text-sm">
          Showing <span className="font-semibold">{from}</span>–
          <span className="font-semibold">{to}</span> of{' '}
          <span className="font-semibold">{totalCount}</span> visitors
        </div>
        {totalPages > 1 ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`px-2.5 py-1.5 rounded-lg border text-sm disabled:opacity-40 ${
                darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'
              }`}>
              First
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg border disabled:opacity-40 ${
                darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'
              }`}>
              <FaChevronLeft />
            </button>
            {pages.map(p => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  p === currentPage
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                    : darkMode
                    ? 'border border-gray-600 hover:bg-gray-700'
                    : 'border border-gray-300 hover:bg-gray-100'
                }`}>
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg border disabled:opacity-40 ${
                darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'
              }`}>
              <FaChevronRight />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={`px-2.5 py-1.5 rounded-lg border text-sm disabled:opacity-40 ${
                darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'
              }`}>
              Last
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const isInRange = value => {
    if (!filterFrom && !filterTo) return true;
    const t = new Date(value).getTime();
    if (isNaN(t)) return false;
    const fromT = filterFrom
      ? new Date(filterFrom).setHours(0, 0, 0, 0)
      : -Infinity;
    const toT = filterTo
      ? new Date(filterTo).setHours(23, 59, 59, 999)
      : Infinity;
    return t >= fromT && t <= toT;
  };
  const hasRange = !!(filterFrom || filterTo);
  const visitsDuring = visits.filter(v => isInRange(v.visitedAt));
  const otherVisits = visits.filter(v => !isInRange(v.visitedAt));

  const renderVisitCard = visit => (
    <div
      key={visit._id}
      className={`border rounded-lg p-4 ${
        darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
      }`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            {formatDisplayDateTime(visit.visitedAt)}
          </span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor[visit.response]}`}>
            {visit.response}
          </span>
          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Lead: {visit.leadStatus}
          </span>
        </div>
        <div className={`flex items-center gap-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {!isAssigned(visit) ? (
            <button
              onClick={() => openAssignModal(visit)}
              className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold shadow-sm">
              <FaUserPlus /> Assign
            </button>
          ) : canManageVisit(visit) ? (
            <>
              <button title="Edit visit date" onClick={() => openVisitModal(visit)}><FaEdit /></button>
              <button
                title="Update client response"
                onClick={() => openResponseModal(visit)}
                className="hover:text-indigo-500">
                <FaClipboardList />
              </button>
              <button title="Delete" onClick={() => deleteVisit(visit)} className="hover:text-red-500"><FaTrash /></button>
            </>
          ) : (
            <span
              className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
              title="Only a salesperson who attended this visit can edit it or update the client response">
              View only
            </span>
          )}
        </div>
      </div>
      {visit.salespersons?.length ? (
        <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Attended: {visit.salespersons.map(s => s.name).join(', ')}
        </p>
      ) : null}
      {visit.itemsOfInterest?.length ? (
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Interested: {visit.itemsOfInterest.join(', ')}
        </p>
      ) : null}
      {visit.quotation ? (
        <p className={`text-sm mt-1 ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
          Quotation: {visit.quotation?.id || 'linked'}
        </p>
      ) : null}
      {visit.remarks ? (
        <p className={`text-sm italic mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {visit.remarks}
        </p>
      ) : null}
    </div>
  );

  return (
    <div
      className={`p-3 sm:p-4 md:p-6 shadow-xl rounded-xl md:rounded-2xl transition-colors duration-300 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div
          className={`flex items-center justify-between p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl transition-colors duration-300 ${
            darkMode
              ? 'bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900'
              : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
          }`}>
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-2.5 md:p-3 rounded-lg">
              <FaUserPlus className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                Visitor Management
              </h2>
              <p className="text-indigo-100 text-xs sm:text-sm mt-0.5">
                Capture visitors and their interactions
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2 rounded-lg">
            <span className="text-white font-medium">{isEditing ? 'Edit Mode' : 'Create Mode'}</span>
          </div>
        </div>
      </div>

      {/* ── ADD / EDIT FORM ── */}
      <div className={`mb-4 sm:mb-6 p-4 sm:p-6 rounded-lg md:rounded-xl border transition-colors duration-300 ${cardCls}`}>
        <h3 className={`text-xl sm:text-2xl font-semibold mb-4 flex items-center ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          <FaUserPlus className="w-5 h-5 mr-2 text-indigo-500" />
          {isEditing ? 'Edit Visitor' : 'Add New Visitor'}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex flex-col">
            <label className={labelCls}>Salutation</label>
            <Select
              value={SALUTATION_OPTIONS.find(o => o.value === form.salutation) || null}
              onChange={opt => setForm(prev => ({...prev, salutation: opt?.value || ''}))}
              options={SALUTATION_OPTIONS}
              isClearable
              placeholder="Select salutation"
              styles={customSelectStyles(darkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(prev => ({...prev, name: e.target.value.toUpperCase()}))}
              placeholder="Enter visitor name"
              className={`${inputCls} uppercase`}
              style={{textTransform: 'uppercase'}}
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>Primary Mobile <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.primaryMobile}
              onChange={e => setForm(prev => ({...prev, primaryMobile: e.target.value.replace(/\D/g, '')}))}
              maxLength={10}
              placeholder="10-digit mobile"
              className={inputCls}
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>Secondary Mobile</label>
            <input
              type="text"
              value={form.secondaryMobile}
              onChange={e => setForm(prev => ({...prev, secondaryMobile: e.target.value.replace(/\D/g, '')}))}
              maxLength={10}
              placeholder="Alternate number"
              className={inputCls}
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(prev => ({...prev, email: e.target.value}))}
              placeholder="Enter email"
              className={inputCls}
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>Date of Birth</label>
            <input
              type="date"
              value={form.dob}
              onChange={e => setForm(prev => ({...prev, dob: e.target.value}))}
              className={inputCls}
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>Station</label>
            <Select
              value={stationOptions.find(o => o.value === form.station) || null}
              onChange={opt => setForm(prev => ({...prev, station: opt?.value || ''}))}
              options={stationOptions}
              isClearable
              placeholder="Select station"
              styles={customSelectStyles(darkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>PIN Code</label>
            <input
              type="text"
              value={form.pinCode}
              onChange={e => setForm(prev => ({...prev, pinCode: e.target.value.replace(/\D/g, '')}))}
              maxLength={6}
              placeholder="Enter pin code"
              className={inputCls}
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>Address</label>
            <input
              type="text"
              value={form.address}
              onChange={e => setForm(prev => ({...prev, address: e.target.value}))}
              placeholder="Enter address"
              className={inputCls}
            />
          </div>

          {/* GST / PAN (like Customer Management) */}
          {!showGstPanFields ? (
            <>
              <div className="flex flex-col">
                <label className={labelCls}>GST Verification</label>
                <button
                  type="button"
                  onClick={() => setShowGSTVerification(true)}
                  className="px-4 py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white">
                  <span>🔍</span>
                  <span>Verify GST Number</span>
                </button>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Verify GSTIN and auto-extract PAN
                </p>
              </div>
              <div className="flex flex-col">
                <label className={labelCls}>PAN Verification</label>
                <button
                  type="button"
                  onClick={() => setShowPANVerification(true)}
                  className="px-4 py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white">
                  <span>🔍</span>
                  <span>Verify PAN Number</span>
                </button>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Verify PAN number independently
                </p>
              </div>
            </>
          ) : (
            <>
              {/* GST */}
              <div className="flex flex-col">
                <label className={labelCls}>
                  GST No {isGstReadOnly && <span className="text-xs text-blue-500">(Verified) 🔒</span>}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={form.gstNo}
                    onChange={handleGSTChange}
                    maxLength={15}
                    disabled={isGstReadOnly}
                    placeholder="15-digit GST"
                    className={`${inputCls} uppercase`}
                  />
                  {form.gstNo ? (
                    <button
                      type="button"
                      onClick={handleClearGST}
                      className={`px-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                      title="Clear GST and PAN">
                      <FaTimes />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setShowGSTVerification(true)}
                    disabled={isGstReadOnly}
                    className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold whitespace-nowrap disabled:opacity-50">
                    {isGstReadOnly ? '✅ Verified' : '🔍 Verify'}
                  </button>
                </div>
              </div>

              {/* PAN */}
              <div className="flex flex-col">
                <label className={labelCls}>
                  PAN No {isPanReadOnly && <span className="text-xs text-blue-500">(From GST) 🔒</span>}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={form.panNo}
                    onChange={handlePANChange}
                    maxLength={10}
                    disabled={isPanReadOnly}
                    placeholder="ABCDE1234F"
                    className={`${inputCls} uppercase`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPANVerification(true)}
                    disabled={isPanReadOnly}
                    className="px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold whitespace-nowrap disabled:opacity-50">
                    {isPanReadOnly ? '✅ From GST' : '🔍 Verify'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Reference */}
          <div className="flex flex-col">
            <label className={labelCls}>Reference</label>
            <button
              type="button"
              onClick={() => setShowReferenceModal(true)}
              className={`w-full flex items-center justify-between border rounded-lg p-2.5 h-[42px] ${
                darkMode ? 'border-gray-500 bg-gray-600 text-white' : 'border-gray-300 bg-white'
              }`}>
              <span className={form.referenceName ? (darkMode ? 'text-gray-100' : 'text-gray-800') : 'text-gray-400'}>
                {form.referenceName || 'Select reference'}
              </span>
              <FaHandshake className="text-orange-500" />
            </button>
          </div>

          {/* Salesperson */}
          <div className="flex flex-col">
            <label className={labelCls}>Salesperson</label>
            <Select
              value={employeeOptions.find(o => o.value === form.salesperson) || null}
              onChange={opt => setForm(prev => ({...prev, salesperson: opt?.value || ''}))}
              options={employeeOptions}
              isClearable
              placeholder="Select salesperson"
              styles={customSelectStyles(darkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="flex flex-col">
            <label className={labelCls}>Remarks</label>
            <input
              type="text"
              value={form.remarks}
              onChange={e => setForm(prev => ({...prev, remarks: e.target.value}))}
              placeholder="Enter remarks"
              className={inputCls}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-5">
          <button
            onClick={saveVisitor}
            disabled={isSubmitting}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg transform hover:scale-105 transition-all disabled:opacity-50">
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Visitor' : 'Add Visitor'}
          </button>
          {isEditing ? (
            <button
              onClick={resetForm}
              className={`px-5 py-2.5 rounded-lg font-semibold shadow-md ${
                darkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-400 hover:bg-gray-500 text-white'
              }`}>
              Cancel
            </button>
          ) : null}
        </div>
      </div>

      {/* ── SEARCH + PAGE SIZE ── */}
      <div
        className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg md:rounded-xl border transition-colors duration-300 ${
          darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
        }`}>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <input
            type="text"
            placeholder="Search by name, mobile, or email..."
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className={`${inputCls} sm:w-1/2`}
          />
          <Select
            value={pageSizeOptions.find(o => o.value === pageSize)}
            onChange={opt => {
              setPageSize(opt.value);
              setCurrentPage(1);
            }}
            options={pageSizeOptions}
            styles={customSelectStyles(darkMode)}
            className="w-full sm:w-1/4"
            isSearchable={false}
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <div className="flex flex-col">
            <label className={labelCls}>From Date</label>
            <input
              type="date"
              value={filterFrom}
              onChange={e => {
                setFilterFrom(e.target.value);
                setCurrentPage(1);
              }}
              className={inputCls}
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>To Date</label>
            <input
              type="date"
              value={filterTo}
              onChange={e => {
                setFilterTo(e.target.value);
                setCurrentPage(1);
              }}
              className={inputCls}
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>Station</label>
            <Select
              value={stationOptions.find(o => o.value === filterStation) || null}
              onChange={opt => {
                setFilterStation(opt?.value || '');
                setCurrentPage(1);
              }}
              options={stationOptions}
              isClearable
              placeholder="All stations"
              styles={customSelectStyles(darkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>Response</label>
            <Select
              value={
                filterResponse
                  ? {value: filterResponse, label: filterResponse}
                  : null
              }
              onChange={opt => {
                setFilterResponse(opt?.value || '');
                setCurrentPage(1);
              }}
              options={RESPONSES.concat('Pending').map(r => ({value: r, label: r}))}
              isClearable
              placeholder="All responses"
              styles={customSelectStyles(darkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>Lead Status</label>
            <Select
              value={
                filterLeadStatus
                  ? {value: filterLeadStatus, label: filterLeadStatus}
                  : null
              }
              onChange={opt => {
                setFilterLeadStatus(opt?.value || '');
                setCurrentPage(1);
              }}
              options={LEAD_STATUSES.map(l => ({value: l, label: l}))}
              isClearable
              placeholder="All lead statuses"
              styles={customSelectStyles(darkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="flex flex-col justify-end">
            <label
              className={`flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2.5 text-sm font-semibold ${
                filterAttendedByMe
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : darkMode
                  ? 'border-gray-500 text-gray-200'
                  : 'border-gray-300 text-gray-700'
              }`}>
              <input
                type="checkbox"
                checked={filterAttendedByMe}
                onChange={e => {
                  setFilterAttendedByMe(e.target.checked);
                  setCurrentPage(1);
                }}
              />
              Attended by me
            </label>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                setFilterFrom('');
                setFilterTo('');
                setFilterStation('');
                setFilterResponse('');
                setFilterLeadStatus('');
                setFilterAttendedByMe(false);
                setSearchQuery('');
                setCurrentPage(1);
              }}
              className={`w-full px-3 py-2.5 rounded-lg text-sm font-semibold border ${
                darkMode
                  ? 'border-gray-500 text-gray-200 hover:bg-gray-600'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* ── LIST (desktop table) ── */}
      <div className="overflow-x-auto hidden lg:block">
        <div className={`rounded-xl shadow-lg border overflow-hidden ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
          <table className="w-full">
            <thead className={`border-b ${darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-gray-200'}`}>
              <tr>
                {['Visitor ID', 'Name', 'Mobile', 'Reference', 'Latest Response', 'Lead Status', 'Visits', 'Total Visits', 'Actions'].map(h => (
                  <th key={h} className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
              {loading ? (
                <tr><td colSpan="9" className="text-center py-6 text-gray-400">Loading...</td></tr>
              ) : visitors.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-6 text-gray-400">No visitors found</td></tr>
              ) : (
                visitors.map((visitor, index) => (
                  <tr key={visitor._id} className={darkMode ? (index % 2 ? 'bg-gray-700/50' : 'bg-gray-700') : index % 2 ? 'bg-gray-50/50' : 'bg-white'}>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{visitor.id}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      {visitor.salutation ? `${visitor.salutation} ` : ''}{visitor.name}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{visitor.primaryMobile}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{visitor.reference?.name || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {visitor.latestVisit?.response ? (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor[visitor.latestVisit.response]}`}>
                          {visitor.latestVisit.response}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{visitor.latestVisit?.leadStatus || '—'}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{visitor.latestVisit?.rangeVisitCount ?? visitor.latestVisit?.visitCount ?? 0}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{visitor.latestVisit?.totalVisitCount ?? visitor.latestVisit?.visitCount ?? 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openInteractions(visitor)}
                          className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-3 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-700 flex items-center gap-1 shadow-sm text-xs">
                          <FaEye /> Interactions
                        </button>
                        <button
                          onClick={() => startEditing(visitor)}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center gap-1 shadow-sm text-xs">
                          <FaEdit /> Edit
                        </button>
                        <button
                          onClick={() => deleteVisitor(visitor)}
                          className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-2 rounded-lg hover:from-red-600 hover:to-red-700 flex items-center gap-1 shadow-sm text-xs">
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── LIST (mobile cards) ── */}
      <div className="block lg:hidden mt-4 space-y-3">
        {visitors.map(visitor => (
          <div
            key={visitor._id}
            className={`border rounded-xl p-4 shadow-sm ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1 min-w-0 pr-2">
                <div className={`font-semibold text-base truncate ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  {visitor.salutation ? `${visitor.salutation} ` : ''}{visitor.name}
                </div>
                <div className={`text-xs font-medium ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                  {visitor.id} • {visitor.primaryMobile}
                </div>
                <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Visits: {visitor.latestVisit?.rangeVisitCount ?? visitor.latestVisit?.visitCount ?? 0}
                  {' · '}Total: {visitor.latestVisit?.totalVisitCount ?? visitor.latestVisit?.visitCount ?? 0}
                </div>
              </div>
              {visitor.latestVisit?.response ? (
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor[visitor.latestVisit.response]}`}>
                  {visitor.latestVisit.response}
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => openInteractions(visitor)} className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-3 py-2 rounded-lg text-xs flex items-center gap-1">
                <FaEye /> Interactions
              </button>
              <button onClick={() => startEditing(visitor)} className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-lg text-xs flex items-center gap-1">
                <FaEdit /> Edit
              </button>
              <button onClick={() => deleteVisitor(visitor)} className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-2 rounded-lg text-xs flex items-center gap-1">
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {renderPagination()}

      {/* ── INTERACTIONS MODAL ── */}
      {selected ? (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
          <div
            className={`w-full max-w-3xl my-8 rounded-2xl overflow-hidden shadow-2xl ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
              <div className="min-w-0">
                <h3 className="font-bold text-white text-lg truncate">
                  {selected.name} — Interactions
                </h3>
                <p className="text-indigo-100 text-xs mt-0.5 truncate">
                  {selected.primaryMobile}
                  {selected.reference?.name ? ` • Ref: ${selected.reference.name}` : ''}
                  {selected.dob ? ` • DOB ${formatDisplayDate(selected.dob)}` : ''}
                </p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-white/80 hover:text-white flex-shrink-0 ml-3">
                <FaTimes />
              </button>
            </div>

            <div
              className={`p-5 max-h-[70vh] overflow-y-auto ${
                darkMode ? 'bg-gray-800' : 'bg-gray-50'
              }`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                {filterFrom || filterTo ? (
                  <span
                    className={`text-xs rounded-full px-3 py-1 ${
                      darkMode
                        ? 'bg-indigo-900/40 text-indigo-200'
                        : 'bg-indigo-50 text-indigo-700'
                    }`}>
                    Has visits in {filterFrom ? formatDisplayDate(filterFrom) : 'start'} –{' '}
                    {filterTo ? formatDisplayDate(filterTo) : 'now'}
                    <span className="text-gray-400"> · grouped below</span>
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">All interactions</span>
                )}
                <button
                  onClick={() => openVisitModal(null)}
                  className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-teal-600 text-white px-3 py-2 rounded-lg text-sm font-semibold shadow-sm">
                  <FaPlus /> Add Interaction
                </button>
              </div>

              {visits.length === 0 ? (
                <p className={`text-sm py-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                  No interactions recorded yet.
                </p>
              ) : (
                <div className="space-y-5">
                  <div>
                    <h4
                      className={`text-xs font-bold uppercase tracking-wide mb-2 ${
                        darkMode ? 'text-indigo-300' : 'text-indigo-700'
                      }`}>
                      {hasRange
                        ? `Visits during selected duration (${visitsDuring.length})`
                        : `All visits (${visits.length})`}
                    </h4>
                    {visitsDuring.length === 0 ? (
                      <p className="text-sm py-2 text-gray-400">
                        No visits in this range.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {visitsDuring.map(renderVisitCard)}
                      </div>
                    )}
                  </div>
                  {hasRange && otherVisits.length > 0 ? (
                    <div>
                      <h4
                        className={`text-xs font-bold uppercase tracking-wide mb-2 ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Other visits ({otherVisits.length})
                      </h4>
                      <div className="space-y-3">
                        {otherVisits.map(renderVisitCard)}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* ── VISIT DETAILS MODAL ── */}
      {showVisitModal && selected ? (
        <VisitModal
          darkMode={darkMode}
          title={editingVisit ? 'Edit Visit Details' : 'New Visit'}
          onClose={() => setShowVisitModal(false)}>
          <div className="space-y-4">
            <div className="flex flex-col">
              <label className={labelCls}>Visit Date &amp; Time</label>
              <input
                type="datetime-local"
                value={visitForm.visitedAt}
                onChange={e => setVisitForm(f => ({...f, visitedAt: e.target.value}))}
                className={inputCls}
              />
            </div>

            {editingVisit ? (
              <>
                <div className="flex flex-col">
                  <label className={labelCls}>Salespersons Attended</label>
                  <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {(editingVisit.salespersons || []).map(s => s.name).join(', ') || '—'}
                  </p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Only the visit date can be changed here.
                  </p>
                </div>
                {editingVisit.itemsOfInterest?.length ? (
                  <div className="flex flex-col">
                    <label className={labelCls}>Items of Interest</label>
                    <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {editingVisit.itemsOfInterest.join(', ')}
                    </p>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="flex flex-col">
                  <label className={labelCls}>Salespersons Attended</label>
                  <Select
                    isMulti
                    value={employeeOptions.filter(o => visitForm.salespersons.includes(o.value))}
                    onChange={opts => setVisitForm(f => ({...f, salespersons: (opts || []).map(o => o.value)}))}
                    options={employeeOptions}
                    placeholder="Select salespersons"
                    styles={customSelectStyles(darkMode)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Only a salesperson added here can edit this visit or update its client response.
                  </p>
                </div>
                <Chips
                  darkMode={darkMode}
                  label="Items of Interest"
                  options={Array.from(new Set([...ITEM_PRESETS, ...visitForm.itemsOfInterest]))}
                  selected={visitForm.itemsOfInterest}
                  onToggle={item =>
                    setVisitForm(f => ({
                      ...f,
                      itemsOfInterest: f.itemsOfInterest.includes(item)
                        ? f.itemsOfInterest.filter(i => i !== item)
                        : [...f.itemsOfInterest, item],
                    }))
                  }
                />
              </>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setShowVisitModal(false)} className={`px-4 py-2 rounded-lg text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button
              onClick={saveVisit}
              disabled={visitSubmitting}
              className="px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold disabled:opacity-50">
              {visitSubmitting ? 'Saving...' : 'Save Visit'}
            </button>
          </div>
        </VisitModal>
      ) : null}

      {/* ── CLIENT RESPONSE MODAL ── */}
      {showResponseModal && responseVisit ? (
        <VisitModal
          darkMode={darkMode}
          title="Update Client Response"
          onClose={() => setShowResponseModal(false)}>
          <div className="space-y-4">
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Visit on {formatDisplayDate(responseVisit.visitedAt)}
            </div>
            <Chips
              darkMode={darkMode}
              label="Client Response"
              options={RESPONSES}
              selected={responseForm.response ? [responseForm.response] : []}
              onToggle={r => setResponseForm(f => ({...f, response: f.response === r ? '' : r}))}
            />
            <div className="flex flex-col">
              <label className={labelCls}>Remarks</label>
              <textarea
                rows={2}
                value={responseForm.remarks}
                onChange={e =>
                  setResponseForm(f => ({...f, remarks: e.target.value}))
                }
                className={inputCls}
                placeholder="Enter remarks"
              />
            </div>
            {responseForm.response === 'Positive' ? (
              <Chips
                darkMode={darkMode}
                label="Lead Status"
                options={LEAD_STATUSES}
                selected={[responseForm.leadStatus]}
                onToggle={s => setResponseForm(f => ({...f, leadStatus: s}))}
              />
            ) : null}
            {responseForm.response === 'Positive' &&
            QUOTATION_LEAD_STATUSES.includes(responseForm.leadStatus) ? (
              <div className="flex flex-col">
                <label className={labelCls}>
                  Quotation <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowQuotationModal(true)}
                  className={`w-full flex items-center justify-between border rounded-lg p-2.5 h-[42px] ${
                    darkMode
                      ? 'border-gray-500 bg-gray-600 text-white'
                      : 'border-gray-300 bg-white'
                  }`}>
                  <span
                    className={
                      responseForm.quotation
                        ? darkMode
                          ? 'text-gray-100'
                          : 'text-gray-800'
                        : 'text-gray-400'
                    }>
                    {responseForm.quotationLabel || 'Select quotation'}
                  </span>
                  <span className="text-gray-400">▾</span>
                </button>
                {responseForm.quotation ? (
                  <button
                    type="button"
                    onClick={() =>
                      setResponseForm(f => ({
                        ...f,
                        quotation: '',
                        quotationType: '',
                        quotationLabel: '',
                      }))
                    }
                    className="self-start text-xs text-gray-400 hover:text-red-500 mt-1">
                    Clear quotation
                  </button>
                ) : null}
                <p
                  className={`text-xs mt-1 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Links this visit's lead status to the selected quotation.
                </p>
              </div>
            ) : null}
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setShowResponseModal(false)} className={`px-4 py-2 rounded-lg text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button
              onClick={saveResponse}
              disabled={responseSubmitting}
              className="px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-semibold disabled:opacity-50">
              {responseSubmitting ? 'Saving...' : 'Save Response'}
            </button>
          </div>
        </VisitModal>
      ) : null}

      {/* ── ASSIGN SALESPERSON MODAL ── */}
      {showAssignModal && assignVisit ? (
        <VisitModal
          darkMode={darkMode}
          title="Assign Salesperson"
          onClose={() => setShowAssignModal(false)}>
          <div className="space-y-4">
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Visit on {formatDisplayDate(assignVisit.visitedAt)} — no salesperson assigned yet.
            </div>
            <div className="flex flex-col">
              <label className={labelCls}>Salespersons Attended <span className="text-red-500">*</span></label>
              <Select
                isMulti
                value={employeeOptions.filter(o => assignSalespersons.includes(o.value))}
                onChange={opts => setAssignSalespersons((opts || []).map(o => o.value))}
                options={employeeOptions}
                placeholder="Select salespersons"
                styles={customSelectStyles(darkMode)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Once assigned, only these salespersons can edit the visit or update the client response.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setShowAssignModal(false)} className={`px-4 py-2 rounded-lg text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button
              onClick={saveAssign}
              disabled={assignSubmitting}
              className="px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold disabled:opacity-50">
              {assignSubmitting ? 'Saving...' : 'Assign'}
            </button>
          </div>
        </VisitModal>
      ) : null}

      {/* ── MODALS ── */}
      <MasterSelection
        isOpen={showReferenceModal}
        onClose={() => setShowReferenceModal(false)}
        onSelect={item => {
          setForm(prev => ({...prev, reference: item._id, referenceName: item.name}));
          setShowReferenceModal(false);
        }}
        allowedTypes={['Reference']}
        defaultType="Reference"
        title="Select Reference"
      />

      <QuotationSelection
        isOpen={showQuotationModal}
        onClose={() => setShowQuotationModal(false)}
        onSelect={q => {
          setResponseForm(f => ({
            ...f,
            quotation: q._id,
            quotationType: q.type,
            quotationLabel: `${q.id || ''} • ${q.recipientName || ''}`.trim(),
          }));
        }}
        companyId={companyId}
        title="Select Quotation"
      />

      <GSTVerificationModal
        isOpen={showGSTVerification}
        onClose={() => setShowGSTVerification(false)}
        onDataReceived={handleGSTVerificationData}
        initialGSTIN={form.gstNo}
      />

      <PANVerificationModal
        isOpen={showPANVerification}
        onClose={() => setShowPANVerification(false)}
        onDataReceived={handlePANVerificationData}
        initialPAN={form.panNo}
      />
    </div>
  );
};

const Chips = ({label, options, selected, onToggle, darkMode}) => (
  <div>
    <label className={`font-semibold mb-1 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map(option => (
        <button
          key={option}
          type="button"
          onClick={() => onToggle(option)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
            selected.includes(option)
              ? 'bg-indigo-600 text-white border-indigo-600'
              : darkMode
              ? 'bg-gray-600 text-gray-200 border-gray-500'
              : 'bg-gray-50 text-gray-600 border-gray-300'
          }`}>
          {option}
        </button>
      ))}
    </div>
  </div>
);

const VisitModal = ({title, children, onClose, darkMode}) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
    <div className={`w-full max-w-2xl my-8 rounded-2xl overflow-hidden shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
        <h3 className="font-bold text-white text-lg">{title}</h3>
        <button onClick={onClose} className="text-white/80 hover:text-white"><FaTimes /></button>
      </div>
      <div className={`p-5 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>{children}</div>
    </div>
  </div>
);

export default VisitorManagement;
